import { assetTokenRatesAtom, assetVariantAtom, assetsAtom, nostraTokenBalancesAtom } from '../lendAndBorrow';
import { Decimal } from '../../datastructures';
import { Asset } from '../../interfaces';
import { createAsyncAtom } from '../../lib/state';
import { walletAddressAtom } from '../wallet';
import accountsAtom from '../lendAndBorrow/accountsAtom';
import { getAccountAddressFromId, getNostraTokenFromFlags } from '../../utils';

export interface LendAndBorrowPosition {
  lendBalanceUsd: Decimal;
  debtBalanceUsd: Decimal;
  lendApy: Decimal;
  borrowApr: Decimal;
  asset: Asset;
  projectedYield: Decimal;
  accountId: number;
}

type LendAndBorrowPositions = {
  [account: string]: LendAndBorrowPosition[];
};

const lendAndBorrowPositionsAtom = createAsyncAtom(async get => {
  const assets = await get(assetsAtom);
  const assetVariants = await get(assetVariantAtom);
  const walletAddress = await get(walletAddressAtom);
  const accounts = await get(accountsAtom);
  const assetTokenRates = await get(assetTokenRatesAtom);
  const nostraTokenBalances = await get(nostraTokenBalancesAtom);

  if (!assets || !assetVariants || !walletAddress) {
    return null;
  }

  const lendAndBorrowPositionsMap: LendAndBorrowPositions = {};

  accounts.forEach((account, accountId) => {
    if (!account) {
      return;
    }

    const accountAddress = getAccountAddressFromId(walletAddress, accountId);

    lendAndBorrowPositionsMap[accountAddress] = [];

    assets.forEach(asset => {
      const assetVariant = assetVariants[`${walletAddress}-${accountId}-${asset.address}`];
      if (!assetVariant) {
        return;
      }

      const tokenRate = assetTokenRates.rate[asset.address];
      if (!tokenRate) {
        return;
      }

      if (!asset.supplyApy || !asset.borrowApr) {
        return;
      }

      const nostraToken = getNostraTokenFromFlags(asset, {
        interestBearing: assetVariant.isLending,
        collateral: assetVariant.isCollateral,
      });

      const lendTokenBalance = nostraTokenBalances[`${walletAddress}-${accountId}-${nostraToken.address}`];
      const debtTokenBalance = nostraTokenBalances[`${walletAddress}-${accountId}-${asset.debtTokenAddress}`];
      if (!lendTokenBalance || !debtTokenBalance) {
        return;
      }

      const lendBalanceUsd = lendTokenBalance.mul(tokenRate);
      const debtBalanceUsd = debtTokenBalance.mul(tokenRate);

      if (lendBalanceUsd.isZero() && debtBalanceUsd.isZero()) {
        return;
      }

      const projectedLendApy = lendBalanceUsd.mul(asset.supplyApy).div(12);
      const projectedBorrowApy = debtBalanceUsd.mul(asset.borrowApr).div(12);

      lendAndBorrowPositionsMap[accountAddress].push({
        lendBalanceUsd: lendBalanceUsd,
        debtBalanceUsd: debtBalanceUsd,
        lendApy: asset.supplyApy,
        borrowApr: asset.borrowApr,
        asset: asset,
        projectedYield: projectedLendApy.sub(projectedBorrowApy),
        accountId: accountId,
      });
    });

    lendAndBorrowPositionsMap[accountAddress].sort((a, b) =>
      b.lendBalanceUsd.gt(a.lendBalanceUsd) ? 1 : b.lendBalanceUsd.lt(a.lendBalanceUsd) ? -1 : 0,
    );
  });

  return lendAndBorrowPositionsMap;
});
export default lendAndBorrowPositionsAtom;
